import React from "react";
import { useTranslation } from "react-i18next";
import {
  FaClipboardList,
  FaGasPump,
  FaMapMarkerAlt,
  FaSeedling,
  FaSatellite,
} from "react-icons/fa";
import FieldAppCalibrationCheckSummary from "./FieldAppCalibrationCheckSummary";
import {
  CALIBRATION_AVERAGE_KEYS,
} from "./calibrationCheckAverages";
import {
  eventLabel,
  fieldLabel,
  formatDetailValue,
  recordTypeLabel,
} from "./fieldAppDetailLabels";

function formatDateTime(value) {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  } catch (error) {
    return String(value);
  }
}

function detailIcon(recordType, eventType) {
  if (recordType === "fuel_record" || eventType === "ABASTECIMENTO") {
    return FaGasPump;
  }
  if (
    recordType === "input_refill_work" ||
    recordType === "input_refill_record"
  ) {
    return FaSeedling;
  }
  if (recordType === "telemetry_sample" || eventType === "TELEMETRIA_AMOSTRA") {
    return FaSatellite;
  }
  if (eventType && eventType.indexOf("TELEMETRIA") === 0) {
    return FaMapMarkerAlt;
  }
  return FaClipboardList;
}

const CALIBRATION_ORDER = [
  "tipo_evento",
  "id_maquina",
  "nome_maquina",
  "informacoes_produto",
  "produto",
  "taxa_alvo_l_ha",
  "quantidade_por_abastecimento",
  "adubo_kg",
  "semente_quantidade",
  "semente_unidade",
  "hectares_painel_inicial",
  "hectares_painel",
  "hectares_plantados",
  "hectares_feitos",
  "taxa_aplicada_semente_mil_ha",
  "sementes_por_metro_aplicadas",
  "inoculante_l",
  "taxa_alvo_inoculante_l_ha",
  "taxa_aplicada_inoculante_l_ha",
  "taxa_aplicada_l_ha",
  "taxa_alvo_adubo_kg_ha",
  "taxa_aplicada_adubo_kg_ha",
  "taxa_alvo_semente",
  "taxa_aplicada_semente",
  "coleta_adubo_kg_ha",
  "coleta_distancia_m",
  "coleta_espacamento_cm",
  "abastecidas_ultimas_3_contagem",
  "media_ultimas_3_ha_h",
  "media_ultimas_3_hectares",
  "media_ultimas_3_adubo_kg_ha",
  "media_ultimas_3_semente_mil_ha",
  "media_ultimas_3_sementes_m",
  "media_ultimas_3_inoculante_l_ha",
  "abastecidas_total_contagem",
  "media_total_ha_h",
  "media_total_hectares",
  "media_total_adubo_kg_ha",
  "media_total_semente_mil_ha",
  "media_total_sementes_m",
  "media_total_inoculante_l_ha",
  "fora_da_tolerancia",
  "valor_principal",
  "horimetro_maquina",
  "ordem_servico_id",
];

const OPERATIONAL_ORDER = [
  "tipo_evento",
  "id_maquina",
  "nome_maquina",
  "valor_principal",
  "horimetro_maquina",
  "hectares_painel_inicial",
  "hectares_painel",
  "horimetro",
  "horimetro_inicial",
  "horas_ate_proxima",
  "horimetro_alvo",
  "intervalo_horas",
  "tanque_cheio",
  "observacoes",
  "session_id",
];

const INPUT_WORK_ORDER = [
  "nome_trabalho",
  "tipo_operacao",
  "nome_maquina",
  "hectares_painel_inicial",
  "horimetro_inicial",
  "taxa_alvo_adubo_kg_ha",
  "taxa_alvo_semente_kg_ha",
  "taxa_alvo_defensivo_l_ha",
  "iniciado_em_utc",
];

const INPUT_RECORD_ORDER = [
  "nome_trabalho",
  "tipo_operacao",
  "hectares_painel",
  "hectares_bacia",
  "horimetro",
  "adubo_kg",
  "semente_kg",
  "defensivo_l",
  "taxa_alvo_adubo_kg_ha",
  "taxa_alvo_semente_kg_ha",
  "taxa_alvo_defensivo_l_ha",
  "registrado_em_utc",
];

const TELEMETRY_ORDER = [
  "tipo_evento",
  "id_maquina",
  "movimento",
  "fonte_dados",
  "timestamp_utc",
  "velocidade_kmh",
  "velocidade_ms",
  "tc",
  "precisao_gps",
  "localizacao",
];

function pickRows(payload, orderedKeys, skipKeys = []) {
  if (!payload || typeof payload !== "object") return [];
  const rows = [];
  const used = {};
  const skip = new Set(skipKeys);

  orderedKeys.forEach((key) => {
    if (skip.has(key)) return;
    if (payload[key] !== undefined && payload[key] !== null && payload[key] !== "") {
      rows.push({ key, label: fieldLabel(key), value: formatDetailValue(key, payload[key]) });
      used[key] = true;
    }
  });

  Object.keys(payload).forEach((key) => {
    if (used[key] || skip.has(key) || key === "tipo_evento") return;
    const value = payload[key];
    if (value === null || value === undefined || value === "") return;
    rows.push({ key, label: fieldLabel(key), value: formatDetailValue(key, value) });
  });

  return rows;
}

function buildPayloadSections(record) {
  const payload = record.payloadParsed || record.payloadData || {};
  const eventType = record.event_type || payload.tipo_evento;
  const recordType = record.record_type;

  if (payload.erro_ingestao) {
    const original = payload.dados_originais || {};
    return [
      {
        title: "Erro no processamento",
        variant: "danger",
        rows: [
          { label: "Motivo", value: String(payload.erro_ingestao) },
        ],
      },
      {
        title: "Dados recebidos",
        rows: pickRows(original, OPERATIONAL_ORDER),
      },
    ];
  }

  if (recordType === "input_refill_work") {
    return [{ title: "Trabalho de insumos", rows: pickRows(payload, INPUT_WORK_ORDER) }];
  }
  if (recordType === "input_refill_record") {
    return [{ title: "Parada de abastecimento", rows: pickRows(payload, INPUT_RECORD_ORDER) }];
  }
  if (recordType === "telemetry_sample" || eventType === "TELEMETRIA_AMOSTRA") {
    return [{ title: "Telemetria GPS", rows: pickRows(payload, TELEMETRY_ORDER) }];
  }
  if (recordType === "fuel_record" || eventType === "ABASTECIMENTO") {
    return [{ title: "Abastecimento", rows: pickRows(payload, OPERATIONAL_ORDER) }];
  }
  if (recordType === "operational_event") {
    if (eventType === "CHECK_CALIBRAGEM") {
      return [
        {
          title: "Check de calibragem",
          rows: pickRows(payload, CALIBRATION_ORDER, CALIBRATION_AVERAGE_KEYS),
        },
      ];
    }
    const title =
      eventType === "MANUTENCAO" || eventType === "MANUTENCAO_EXECUTADA"
        ? "Manutenção"
        : "Evento operacional";
    return [{ title, rows: pickRows(payload, OPERATIONAL_ORDER) }];
  }

  return [{ title: "Dados do registro", rows: pickRows(payload, OPERATIONAL_ORDER) }];
}

function DetailRow({ label, value }) {
  return (
    <div className="field-app-detail-row">
      <div className="field-app-detail-label">{label}</div>
      <div className="field-app-detail-value">{value}</div>
    </div>
  );
}

function DetailSection({ title, rows, variant }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className={`field-app-detail-section ${variant ? `field-app-detail-section--${variant}` : ""}`}>
      <h4 className="field-app-detail-section-title">{title}</h4>
      <div className="field-app-detail-grid">
        {rows.map((row) => (
          <DetailRow
            key={`${title}-${row.key || row.label}`}
            label={row.label}
            value={row.value}
          />
        ))}
      </div>
    </div>
  );
}

export default function FieldAppRecordDetail({ record }) {
  const { t } = useTranslation();
  if (!record) return null;

  const payload = record.payloadParsed || record.payloadData || {};
  const eventType = record.event_type || payload.tipo_evento;
  const Icon = detailIcon(record.record_type, eventType);
  const sections = buildPayloadSections(record);

  const metaRows = [
    { label: t("Operador"), value: record.operatorName || "—" },
    { label: t("Recebido em"), value: formatDateTime(record.received_at || record.timestamp) },
    { label: t("Tipo"), value: recordTypeLabel(record.record_type) },
    { label: t("Evento"), value: eventLabel(eventType) },
    { label: t("Lote"), value: record.batch_id || "—" },
    { label: t("Id local"), value: record.local_id != null ? String(record.local_id) : "—" },
  ];

  return (
    <div className="field-app-detail">
      <div className="field-app-detail-hero">
        <div className="field-app-detail-icon">
          <Icon />
        </div>
        <div>
          <div className="field-app-detail-badge">{recordTypeLabel(record.record_type)}</div>
          <h3 className="field-app-detail-title">{eventLabel(eventType)}</h3>
          <p className="field-app-detail-subtitle">
            {record.operatorName || t("Operador")} ·{" "}
            {formatDateTime(record.received_at || record.timestamp)}
          </p>
        </div>
      </div>

      <DetailSection title={t("Informações gerais")} rows={metaRows} />

      {eventType === "CHECK_CALIBRAGEM" ? (
        <FieldAppCalibrationCheckSummary payload={payload} t={t} />
      ) : null}

      {sections.map((section) => (
        <DetailSection
          key={section.title}
          title={t(section.title)}
          rows={section.rows}
          variant={section.variant}
        />
      ))}

      {record.panelPhotoUrl && (
        <div className="field-app-detail-section field-app-detail-section--photo">
          <h4 className="field-app-detail-section-title">
            {t("Foto do painel")}
          </h4>
          <p className="field-app-detail-photo-hint">
            {t(
              "Imagem enviada pelo operador no tablet ao registrar abastecimento ou assumir a máquina."
            )}
          </p>
          <a
            href={record.panelPhotoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="field-app-detail-photo-link"
          >
            <img
              src={record.panelPhotoUrl}
              alt={t("Foto do painel do horímetro/hodômetro")}
              className="field-app-detail-photo"
            />
          </a>
        </div>
      )}
    </div>
  );
}

export function fuelRecordToDetail(row) {
  return {
    record_type: "fuel_record",
    event_type: "ABASTECIMENTO",
    operatorName: row.operatorName,
    received_at: row.timestamp,
    panelPhotoUrl: row.panelPhotoUrl || null,
    batch_id: null,
    local_id: null,
    payloadParsed: {
      tipo_evento: "ABASTECIMENTO",
      id_maquina: row.equipmentName,
      valor_principal: row.liters,
      horimetro_maquina: row.hour_meter,
      hectares_painel: row.panel_hectares,
      observacoes: row.observations,
      timestamp_utc: row.timestamp,
    },
  };
}
